import React, { useState, useEffect, useRef } from 'react';
import {
  Grid,
  Typography,
  FormControl,
  FormLabel,
} from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import Form, { FormSection } from 'components/forms/Form';
import { AutocompleteField } from 'components/fields';
import { useFields } from 'hooks/fields';
import { useAuthenticated, useFetch } from 'hooks/fetch';
import { fetchAuthenticated } from 'services/fetch';
import { emitEvent } from 'utils/events';
import { ButtonsForm } from 'components/buttons/ButtonsForm';

const LockForm = (props) => {
  const { classes } = props;
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingEquipments, setIsLoadingEquipments] = useState(false);
  const [isDisabled, setDisabled] = useState(false);
  const [submitForm, setSubmitForm] = useState(false);
  const [equipmentOptions, setEquipmentOptions] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState(null);
  const isInitialLoad = useRef(true);

  const fields = useFields(['equipments', 'company']);

  const { data: companyData } = useAuthenticated(
    useFetch,
    'company?notPaginated=true',
    {}
  );

  useEffect(() => {
    if (companyData && companyData.data) {
      const sorted = companyData.data.sort((a, b) => (a.name > b.name ? 1 : -1));
      setCompanies(sorted);
    }
  }, [companyData]);

  useEffect(() => {
    if (!selectedCompanyId) {
      setEquipmentOptions([]);
      return;
    }

    const fetchEquipments = async () => {
      setIsLoadingEquipments(true);
      try {
        const lockId = props.data && props.data._id ? props.data._id : '';
        const url = `lock/available-equipments?companyId=${selectedCompanyId}${lockId ? `&lockId=${lockId}` : ''}`;
        const response = await fetchAuthenticated('GET', url);
        if (response.ok) {
          const data = await response.json();
          const sorted = (data || [])
            .filter(e => e.imei)
            .sort((a, b) => (a.imei > b.imei ? 1 : -1));
          setEquipmentOptions(sorted);
        }
      } catch (err) {
        console.error('Erro ao buscar equipamentos:', err);
      } finally {
        setIsLoadingEquipments(false);
      }
    };

    fetchEquipments();
  }, [selectedCompanyId]);

  useEffect(() => {
    if (props.data && props.data._id && companies.length > 0) {
      if (props.data.company) {
        const matched = companies.find(c => c._id === props.data.company._id);
        if (matched && fields.company.current && fields.company.current.setFieldValue) {
          fields.company.current.setFieldValue(matched);
          setSelectedCompanyId(matched._id);
        }
      }
    }
  }, [props.data, companies]);

  useEffect(() => {
    if (props.data && props.data._id && equipmentOptions.length > 0) {
      const savedEquipments = props.data.equipments || [];
      const matched = savedEquipments
        .map(saved => equipmentOptions.find(e => e._id === saved._id))
        .filter(Boolean);

      if (fields.equipments.current && fields.equipments.current.setFieldValue) {
        fields.equipments.current.setFieldValue(matched);
      }
      isInitialLoad.current = false;
    }
  }, [props.data, equipmentOptions]);

  const validateForm = async (event) => {
    event.preventDefault();
    setSubmitForm(true);
  };

  useEffect(() => {
    if (submitForm) {
      (async () => { await handleSubmit(); })();
    }
  }, [submitForm]);

  const handleSubmit = async () => {
    setDisabled(true);
    setIsLoading(true);

    const selectedEquipments = fields.equipments.current
      ? fields.equipments.current.raw || []
      : [];

    const selectedCompany = fields.company.current
      ? fields.company.current.raw
      : null;

    const data = {
      equipments: selectedEquipments.map(e => ({
        _id: e._id,
        imei: e.imei,
        code: e.code,
      })),
      company: selectedCompany ? {
        _id: selectedCompany._id,
        name: selectedCompany.name,
      } : null,
    };

    try {
      await props.handleSubmit(data);
      setDisabled(false);
      setIsLoading(false);
      setSubmitForm(false);
    } catch (err) {
      emitEvent('showSnack', { message: 'Erro ao processar formulário', type: 'error' });
      setDisabled(false);
      setIsLoading(false);
      setSubmitForm(false);
    }
  };

  const ReadOnlyField = ({ label, value }) => (
    <Grid item xs={12} sm={6} md={4} lg={3}>
      <FormControl component="fieldset" fullWidth className={classes.fullWidthField}>
        <FormLabel
          component="legend"
          style={{
            transform: 'translate(0, 1.5px) scale(0.75)',
            transformOrigin: 'top left',
          }}
        >
          {label}
        </FormLabel>
        <Typography className={classes.readOnlyValue}>
          {value || '—'}
        </Typography>
      </FormControl>
    </Grid>
  );

  return (
    <Form title={props.title || 'Detalhes do Cadeado'}>
      <form onSubmit={validateForm} noValidate>
        <FormSection title="Informações do Cadeado" divider={true}>
          <Grid container spacing={2} justify="flex-start">
            <ReadOnlyField label="Unit Number" value={props.data && props.data.unitnumber} />
            <ReadOnlyField label="Nome" value={props.data && props.data.name} />
            <ReadOnlyField label="Modelo" value={props.data && props.data.vehiclemodel} />
            <ReadOnlyField label="Cor" value={props.data && props.data.vehiclecolor} />
            <ReadOnlyField label="Endereço" value={props.data && props.data.address} />
            <ReadOnlyField label="Reg Number" value={props.data && props.data.regnumber} />
            <ReadOnlyField
              label="Status"
              value={props.data && (props.data.active === '1' ? 'Ativo' : 'Inativo')}
            />
          </Grid>
        </FormSection>

        <FormSection title="Empresa" divider={true}>
          <Grid container spacing={2} justify="flex-start">
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <FormControl fullWidth className={classes.fullWidthField}>
                <AutocompleteField
                  ref={fields.company}
                  label="Empresa"
                  name="company"
                  options={companies}
                  getOptionLabel={opt => opt.name || ''}
                  getOptionValue={opt => opt._id}
                  noOptionsMessage={() => 'Nenhuma empresa encontrada'}
                  loadingMessage={() => 'Carregando empresas...'}
                  isClearable={true}
                  disabled={isDisabled}
                  onChange={(opt) => {
                    const newCompanyId = opt ? opt._id : null;
                    if (newCompanyId !== selectedCompanyId) {
                      setSelectedCompanyId(newCompanyId);
                      if (!isInitialLoad.current && fields.equipments.current && fields.equipments.current.setFieldValue) {
                        fields.equipments.current.setFieldValue([]);
                      }
                    }
                  }}
                />
              </FormControl>
            </Grid>
          </Grid>
        </FormSection>

        <FormSection title="Equipamentos Vinculados">
          <Grid container spacing={2} justify="flex-start">
            <Grid item xs={12} sm={12} md={8} lg={6}>
              <FormControl fullWidth>
                <AutocompleteField
                  ref={fields.equipments}
                  label="Equipamentos"
                  name="equipments"
                  options={equipmentOptions}
                  isMulti={true}
                  isLoading={isLoadingEquipments}
                  getOptionLabel={opt => opt.imei || ''}
                  getOptionValue={opt => opt._id}
                  getRawValue={opt => opt}
                  noOptionsMessage={() => !selectedCompanyId
                    ? 'Selecione uma empresa primeiro'
                    : 'Nenhum equipamento disponível'
                  }
                  loadingMessage={() => 'Carregando equipamentos...'}
                  isClearable={true}
                  disabled={isDisabled || !selectedCompanyId}
                  placeholder={!selectedCompanyId
                    ? 'Selecione uma empresa primeiro...'
                    : 'Selecione os equipamentos...'
                  }
                  InputLabelProps={{ shrink: true }}
                />
              </FormControl>
            </Grid>
          </Grid>
        </FormSection>

        <ButtonsForm
          handleCancel={props.handleCancel}
          handleSubmit={props.handleSubmit}
          formDisabled={isDisabled}
        />
      </form>
    </Form>
  );
};

const styles = theme => ({
  fullWidthField: {
    [theme.breakpoints.up('sm')]: {
      maxWidth: '85%',
    },
  },
  readOnlyValue: {
    fontSize: 16,
    color: theme.palette.gray ? theme.palette.gray.gray_4 : '#333',
    minHeight: 24,
    paddingTop: 4,
  },
});

export default withStyles(styles)(LockForm);
